import React, { useCallback } from "react";
import { Box, CloseButton, Flex, useToast as useChakraToast } from "@chakra-ui/react";
import styled from "@emotion/styled";
import { colours } from "@/theme/colours";
import { Text } from "@chakra-ui/react";
import { FZCLIcon } from "./Icon";

// FIXME: With the upgrade to react 18 the toast component in it's current form no longer works
// Has been noted as a breaking change in the charka issues: https://github.com/chakra-ui/chakra-ui/pull/5797 and https://github.com/chakra-ui/chakra-ui/issues/5855
// Works sort of with the createStandaloneToast but can't use that in custom component

export interface Props {
	title: string;
	description?: string;
	isClosable?: boolean;
	duration?: number;
	position?: "top" | "top-right" | "top-left" | "bottom" | "bottom-right" | "bottom-left";
	children?: React.ReactElement<unknown, string | React.JSXElementConstructor<unknown>>;
}

const StyledToast = styled(Box)`
	display: flex;
	align-content: start;
	position: relative;
	padding: 12px 32px 12px 12px;
	width: 400px;
	color: ${colours.text.paragraph.regular};
	background-color: ${colours.accent.green["100"]};
	border: 1px solid ${colours.accent.green["300"]};
	border-radius: 5px;
`;

const SuccessToast = styled(StyledToast)`
	background-color: ${colours.accent.green["100"]};
	border: 1px solid ${colours.accent.green["300"]};
`;

const InfoToast = styled(StyledToast)`
	background-color: ${colours.accent.turquoise["100"]};
	border: 1px solid ${colours.accent.turquoise["300"]};
`;

const WarningToast = styled(StyledToast)`
	background-color: ${colours.accent.orange["100"]};
	border: 1px solid ${colours.accent.orange["300"]};
`;

const ErrorToast = styled(StyledToast)`
	background-color: ${colours.accent.red["100"]};
	border: 1px solid ${colours.accent.red["300"]};
`;

interface ToastBodyProps extends Props {
	onClose: () => void;
	children?: React.ReactElement<unknown, string | React.JSXElementConstructor<unknown>>;
}

const ToastBody = ({ title, description, isClosable, onClose, children }: ToastBodyProps) => (
	<>
		<Flex flexDirection={"column"}>
			<Text fontFamily="Roboto" fontWeight="500">
				{title}
			</Text>
			{description && (
				<Text fontFamily="Roboto" fontWeight="400">
					{description}
				</Text>
			)}
			{children}
		</Flex>

		{isClosable && <CloseButton position="absolute" size="sm" right="8px" top="14px" onClick={onClose} data-testid="close-toast" />}
	</>
);

export const useToast = (): {
	close: () => void;
	successToast: (props: Props) => void;
	infoToast: (props: Props) => void;
	warningToast: (props: Props) => void;
	errorToast: (props: Props) => void;
} => {
	const chakraToast = useChakraToast();

	const close = (): void => {
		chakraToast.closeAll();
	};

	// Wrapped in useCallback to prevent unnecessary re-renders when used as a dependency in useEffect
	const successToast = useCallback(
		({ title, description, isClosable, duration, position, children }: Props) =>
			chakraToast({
				duration: duration || 9000,
				position: position || "top",
				render: () => (
					<SuccessToast>
						<FZCLIcon icon="fa-circle-check" color={colours.accent.green[400]} boxSize="16px" mr="12px" mt="3px" />

						<ToastBody title={title} description={description} isClosable={isClosable} onClose={close} children={children} />
					</SuccessToast>
				),
			}),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[]
	);

	const infoToast = useCallback(
		({ title, description, isClosable, duration, position, children }: Props) =>
			chakraToast({
				duration: duration || 9000,
				position: position || "top",
				render: () => (
					<InfoToast>
						<FZCLIcon variant="solid" icon="fa-circle-info" color={colours.accent.turquoise[400]} boxSize="16px" mr="12px" mt="3px" />

						<ToastBody title={title} description={description} isClosable={isClosable} onClose={close} children={children} />
					</InfoToast>
				),
			}),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[]
	);

	const warningToast = useCallback(
		({ title, description, isClosable, duration, position, children }: Props) =>
			chakraToast({
				duration: duration || 9000,
				position: position || "top",
				render: () => (
					<WarningToast>
						<FZCLIcon variant="solid" icon="fa-triangle-exclamation" color={colours.accent.orange[400]} boxSize="16px" mr="12px" mt="3px" />

						<ToastBody title={title} description={description} isClosable={isClosable} onClose={close} children={children} />
					</WarningToast>
				),
			}),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[]
	);

	const errorToast = useCallback(
		({ title, description, isClosable, duration, position, children }: Props) =>
			chakraToast({
				duration: duration || 9000,
				position: position || "top",
				render: () => (
					<ErrorToast>
						<FZCLIcon variant="solid" icon="fa-circle-exclamation" color={colours.accent.red[400]} boxSize="16px" mr="12px" mt="3px" />

						<ToastBody title={title} description={description} isClosable={isClosable} onClose={close} children={children} />
					</ErrorToast>
				),
			}),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[]
	);

	return { close, successToast, infoToast, warningToast, errorToast };
};
